export const INVENTORY_APPLICATION = 'inv';

/** Catalog scopes for the Inventory (`inv`) application. */
export const InventoryPermissions = {
  ProductsRead: 'inv.products.read',
  ProductsWrite: 'inv.products.write',
  StockRead: 'inv.stock.read',
  StockWrite: 'inv.stock.write',
  ContainersRead: 'inv.containers.read',
  ContainersWrite: 'inv.containers.write',
  ProductContainersRead: 'inv.prod-containers.read',
  ProductContainersWrite: 'inv.prod-containers.write',
  SkusRead: 'inv.skus.read',
  SkusWrite: 'inv.skus.write',
  DeliveriesRead: 'inv.deliveries.read',
  DeliveriesWrite: 'inv.deliveries.write',
  DevicesRead: 'inv.devices.read',
  DevicesWrite: 'inv.devices.write',
  OperatorsRead: 'inv.operators.read',
  OperatorsWrite: 'inv.operators.write',
  TradePartnersRead: 'inv.trade-partners.read',
  TradePartnersWrite: 'inv.trade-partners.write',
  RolesRead: 'inv.roles.read',
  RolesWrite: 'inv.roles.write',
  RolesAssign: 'inv.roles.assign',
} as const;

export type InventoryPermission =
  (typeof InventoryPermissions)[keyof typeof InventoryPermissions];

export interface Product {
  id: number;
  productCode: string;
  name: string;
  description: string | null;
  productCategory: string | null;
  parentId: number | null;
  lifespan: string | null;
  leadTime: string | null;
  activeFrom: string;
  activeTo: string | null;
  tenantId: string;
}

export interface CreateProductRequest {
  productCode: string;
  name: string;
  description?: string | null;
  productCategory?: string | null;
  parentId?: number | null;
  lifespan?: string | null;
  leadTime?: string | null;
  activeFrom?: string | null;
  activeTo?: string | null;
}

export interface UpdateProductRequest {
  name: string;
  description?: string | null;
  productCategory?: string | null;
  parentId?: number | null;
  lifespan?: string | null;
  leadTime?: string | null;
  activeTo?: string | null;
}

export interface Container {
  id: number;
  containerCode: string;
  name: string;
  description: string | null;
  iconKey: string | null;
  activeFrom: string;
  activeTo: string | null;
  tenantId: string;
}

export interface CreateContainerRequest {
  containerCode: string;
  name: string;
  description?: string | null;
  iconKey?: string | null;
  activeFrom?: string | null;
  activeTo?: string | null;
}

export interface UpdateContainerRequest {
  name: string;
  description: string | null;
  activeTo: string | null;
  iconKey?: string | null;
}

export interface ProductContainer {
  id: number;
  productId: number;
  productCode: string;
  productName: string;
  containerId: number;
  containerCode: string;
  containerName: string;
  containerIconKey: string | null;
  name: string;
  minQuantity: number | null;
  maxQuantity: number | null;
  measureType: string | null;
  activeFrom: string;
  activeTo: string | null;
  tenantId: string;
  /** Empty = available on all kiosks; otherwise only listed device IDs. */
  allowedDeviceIds: string[];
}

export interface CreateProductContainerRequest {
  productId: number;
  containerId: number;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  measureType?: string | null;
  allowedDeviceIds?: string[] | null;
}

export interface UpdateProductContainerRequest {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  measureType?: string | null;
  allowedDeviceIds?: string[] | null;
}

export interface DevicePeripheral {
  deviceId: string;
  readableId: string;
  name: string;
  deviceType: string;
  role: string;
  isPrimary: boolean;
  host?: string | null;
}

export interface PrintKiosk {
  id: string;
  name: string;
  readableId: string;
  deviceType: string;
  physicalLocation: string | null;
  host: string | null;
  validFrom: string;
  validTo: string | null;
  peripherals?: DevicePeripheral[];
  serverVersion?: string | null;
  updateChannel?: string | null;
  lastHealthReceivedAt?: string | null;
  isHeartbeatStale?: boolean;
}

export interface Sku {
  streamId: string;
  skuCode: string;
  status: string;
  /** Local product code at this store; may differ from the SKU barcode after transfer. */
  currentProductCode: string | null;
  /** Local container code at this store; may differ from origin after transfer. */
  currentContainerCode: string | null;
  locationCode: string | null;
  batchNumber: number | null;
  createdOn: string;
  expiresOn: string | null;
}

export interface SkuEventTimelineItem {
  eventType: string;
  kind: string;
  title: string;
  detail: string | null;
  version: number;
  timestamp: string;
}

export interface SkuDetail {
  streamId: string;
  skuCode: string;
  status: string;
  /** Local product code at this store; may differ from the SKU barcode after transfer. */
  currentProductCode: string | null;
  productName: string | null;
  /** Local container code at this store; may differ from origin after transfer. */
  currentContainerCode: string | null;
  containerName: string | null;
  locationCode: string | null;
  batchNumber: number | null;
  createdOn: string;
  expiresOn: string | null;
  events: SkuEventTimelineItem[];
}

export interface PrintSkusRequest {
  productCode: string;
  containerCode: string;
  quantity: number;
  prepDate?: string | null;
  batchNumber?: number | null;
  parentSku?: string | null;
  deviceId?: string | null;
  printedByAccountId?: string | null;
}

export interface SkusPrintedRow {
  productCode: string | null;
  productName: string;
  containerCode: string | null;
  containerName: string;
  count: number;
  totalMin: number | null;
  totalMax: number | null;
  measure: string | null;
}

export interface SkusPrintedReport {
  startRange: string;
  endRange: string;
  totalCount: number;
  rows: SkusPrintedRow[];
  tenantIds: string[];
}

/** Matches System.DayOfWeek (Sunday = 0 … Saturday = 6). */
export type DayOfWeekNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** 0 = accounts, 1 = self */
export type ReportSubscriptionRecipientMode = 0 | 1;

export interface ReportSubscription {
  id: string;
  reportType: string;
  enabled: boolean;
  coverageDays: DayOfWeekNumber[];
  sendDayOfWeek: DayOfWeekNumber;
  sendTimeUtc: string;
  recipientMode: ReportSubscriptionRecipientMode;
  accountIds: string[];
  recipientEmails: string[];
  createdByAccountId: string;
  createdAt: string;
  lastClaimedPeriodStartUtc: string | null;
  lastSentPeriodStartUtc: string | null;
}

export interface UpsertReportSubscriptionRequest {
  coverageDays: DayOfWeekNumber[];
  sendDayOfWeek: DayOfWeekNumber;
  sendTimeUtc: string;
  accountIds?: string[];
  sendToSelf?: boolean;
  enabled?: boolean;
}

export interface PushCatalogRequest {
  targetTenantIds: string[];
  productCodes?: string[] | null;
  containerCodes?: string[] | null;
  productContainers?: { productCode: string; containerCode: string }[] | null;
  includeLinkedProductContainers?: boolean;
}

export interface CatalogPushResponse {
  correlationId: string;
  stagedTargetTenantIds: string[];
  productCount: number;
  containerCount: number;
  productContainerCount: number;
}

export interface DeliveryItem {
  deliveryItemKey: string;
  itemName: string;
  itemCategory: string | null;
  requestedQuantity: number;
  fulfilledQuantity: number;
  itemPrice: number;
}

export interface DeliveryCharge {
  chargeId: string;
  chargeType: string;
  amount: number;
  note: string | null;
}

export interface Delivery {
  streamId: string;
  dispatchFrom: string;
  dispatchTo: string;
  status: string;
  actualDispatchOn: string | null;
  actualReceivingOn: string | null;
  cancelledOn: string | null;
  items: DeliveryItem[];
  charges: DeliveryCharge[];
}

export interface DeliveryDispatchDestination {
  tradePartnerId: number;
  partnerTenantId: string;
  partnerName: string | null;
  partnerLocationCode: string;
}

export interface CreateDeliveryDefaults {
  dispatchFrom: string;
  destinations: DeliveryDispatchDestination[];
}

export interface CreateDeliveryRequest {
  dispatchTo: string;
  expectedDispatchOn?: string | null;
  expectedReceivingOn?: string | null;
}

export interface AddPackingListItemRequest {
  productContainerId: number;
  requestedQuantity: number;
}

export interface DeliveryPricedProductContainer {
  productContainerId: number;
  productCode: string;
  containerCode: string;
  name: string;
  salePrice: number;
}

export interface AddDeliveryChargeRequest {
  chargeType: string;
  amount: number;
  note?: string | null;
}

export interface CancelDeliveryRequest {
  reason?: string | null;
}

/** Empty Guid used by the API for unredeemed invites. */
export const EMPTY_TENANT_ID = '00000000-0000-0000-0000-000000000000';

export const PartnershipStatus = {
  Pending: 0,
  Agreed: 1,
} as const;

export type PartnershipStatusValue =
  (typeof PartnershipStatus)[keyof typeof PartnershipStatus];

export const PartnershipOrigin = {
  Outbound: 0,
  Inbound: 1,
} as const;

export type PartnershipOriginValue =
  (typeof PartnershipOrigin)[keyof typeof PartnershipOrigin];

export function partnershipStatusLabel(status: number): string {
  switch (status) {
    case PartnershipStatus.Pending:
      return 'Pending';
    case PartnershipStatus.Agreed:
      return 'Agreed';
    default:
      return String(status);
  }
}

export function partnershipOriginLabel(origin: number): string {
  switch (origin) {
    case PartnershipOrigin.Outbound:
      return 'Outbound';
    case PartnershipOrigin.Inbound:
      return 'Inbound';
    default:
      return String(origin);
  }
}

export function isEmptyTenantId(tenantId: string | null | undefined): boolean {
  return !tenantId || tenantId === EMPTY_TENANT_ID;
}

export interface TradePartnerConfiguration {
  defaultToAddresses: string[];
  defaultCcAddresses: string[];
  defaultBccAddresses: string[];
}

export interface TradePartner {
  id: number;
  partnerTenantId: string;
  partnerName: string | null;
  partnerLocationCode: string | null;
  partnershipStatus: PartnershipStatusValue | number;
  origin: PartnershipOriginValue | number;
  inviteExpiresAt: string | null;
  schemaType: string | null;
  configuration: TradePartnerConfiguration | null;
  createdOn: string;
  validFrom: string;
  validTo: string | null;
}

export interface CreateTradePartnerInviteRequest {
  partnerLocationCode: string;
  expiresAt?: string | null;
}

export interface CreateTradePartnerInviteResponse {
  tradePartnerId: number;
  inviteCode: string;
  expiresAt: string | null;
}

export interface RequestTradePartnerRequest {
  partnerLocationCode: string;
  configuration: TradePartnerConfiguration;
}

export interface RedeemTradePartnerInviteRequest {
  partnerLocationCode: string;
  inviteCode: string;
  configuration: TradePartnerConfiguration;
}

export interface RedeemTradePartnerInviteResponse {
  correlationId: string;
  partnerLocationCode: string;
}

export interface ApproveTradePartnerRequest {
  configuration: TradePartnerConfiguration;
}

export interface DeclineTradePartnerRequest {
  reason?: string | null;
}

export interface ProductContainerTradePricing {
  id: string;
  tradePartnerId: number;
  productId: number;
  productCode: string;
  productName: string;
  containerId: number;
  containerCode: string;
  containerName: string;
  productContainerName: string;
  salePrice: number;
  cost: number | null;
  taxRate: number | null;
  validFrom: string;
  validTo: string | null;
}

export interface CreateProductContainerTradePricingRequest {
  productId: number;
  containerId: number;
  salePrice: number;
  cost?: number | null;
  taxRate?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
}

export interface TradePartnerMapping {
  id: string;
  tradePartnerId: number;
  fromProductCode: string;
  fromContainerCode: string;
  mapsToProductId: number;
  mapsToProductCode: string;
  mapsToProductName: string;
  mapsToContainerId: number;
  mapsToContainerCode: string;
  mapsToContainerName: string;
  validFrom: string;
  validTo: string | null;
}

export interface CreateTradePartnerMappingRequest {
  fromProductCode: string;
  fromContainerCode: string;
  mapsToProductId: number;
  mapsToContainerId: number;
  validFrom?: string | null;
  validTo?: string | null;
}

export interface KioskAuthSettings {
  pinLoginEnabled: boolean;
  rfidLoginEnabled: boolean;
  operatorLoginRequiredForPrint: boolean;
}

export interface KioskOperator {
  id: string;
  accountId: string;
  displayName: string;
  initials: string;
  email: string;
  hasPin: boolean;
  hasRfid: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
}

export interface LinkablePlatformAccount {
  accountId: string;
  displayName: string;
  email: string;
  enabled: boolean;
}

export interface LinkKioskOperatorRequest {
  accountId?: string | null;
  email?: string | null;
  displayName?: string | null;
  initials?: string | null;
  pin?: string | null;
  tagUid?: string | null;
  enabled?: boolean;
}

export interface UpdateKioskOperatorRequest {
  displayName: string;
  initials: string;
  enabled: boolean;
}

export interface SetKioskOperatorCredentialsRequest {
  pin?: string | null;
  tagUid?: string | null;
}
